import { type ReactElement } from 'react';
import { SPOT_STATUSES, type MapStats } from '@kratispot/shared';
import { useTranslation } from '@/lib/i18n';
import { STATUS_COPY } from './spot-tile';

const SWATCH: Readonly<Record<string, string>> = {
  free: 'bg-emerald-600',
  reserved: 'bg-amber-500',
  occupied: 'bg-red-500',
  cleaning: 'bg-sky-500',
  occupations: 'bg-violet-600',
};

// Narrow screens drop the words, never the counts, and never wrap. See §20.
function Item({
  tone,
  label,
  count,
}: {
  tone: string;
  label: string;
  count: number;
}): ReactElement {
  return (
    <span
      title={label}
      className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted-foreground"
    >
      <i aria-hidden className={`size-2.5 rounded-[4px] ${tone}`} />
      <span className="hidden sm:inline">{label}</span>
      <b className="rounded-full bg-secondary px-2 py-px text-[11px] font-bold text-foreground">
        {count}
      </b>
    </span>
  );
}

function Occupancy({ pct, bar }: { pct: number; bar: ReactElement[] }): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex shrink-0 items-center gap-2 border-s ps-2.5"
      title={t('occupancy')}
    >
      <span className="flex h-1.5 w-10 overflow-hidden rounded-full bg-secondary sm:w-[74px]">
        {bar}
      </span>
      <b className="text-xs font-bold">{pct}%</b>
    </span>
  );
}

// The legend row is the statistics row: counts plus occupancy, never a second row.
export function MapLegend({ stats }: { stats: MapStats }): ReactElement {
  const { t } = useTranslation();
  const total = stats.total;
  const segment = (key: string, count: number): ReactElement => (
    <i
      key={key}
      aria-hidden
      className={`block h-full ${SWATCH[key] as string}`}
      style={{ width: `${total === 0 ? 0 : (count / total) * 100}%` }}
    />
  );

  return (
    <div
      data-testid="legend"
      className="flex items-center justify-center gap-2 overflow-hidden border-t px-3 py-2.5 sm:gap-3.5"
    >
      {stats.mode === 'live' &&
        SPOT_STATUSES.map((status) => (
          <Item
            key={status}
            tone={SWATCH[status] as string}
            label={t(STATUS_COPY[status])}
            count={stats.counts[status]}
          />
        ))}
      {stats.mode === 'live' && (
        <Occupancy
          pct={stats.occupancyPct}
          bar={(['occupied', 'cleaning', 'reserved'] as const).map((status) =>
            segment(status, stats.counts[status]),
          )}
        />
      )}
      {stats.mode === 'future' && (
        <>
          <Item tone={SWATCH.free as string} label={t('free')} count={stats.free} />
          <Item tone={SWATCH.reserved as string} label={t('reserved')} count={stats.reserved} />
          <Occupancy pct={stats.occupancyPct} bar={[segment('reserved', stats.reserved)]} />
        </>
      )}
      {stats.mode === 'past' && (
        <>
          <Item
            tone={SWATCH.occupations as string}
            label={t('occupations')}
            count={stats.occupations}
          />
          <Item
            tone={SWATCH.reserved as string}
            label={t('reservations')}
            count={stats.reservations}
          />
        </>
      )}
    </div>
  );
}
